import { fimCdnAnimateUrl } from '@utils/fimUtils'
import DOMPurify from 'dompurify'
import parse from 'html-react-parser'
import { useEffect, useRef } from 'react'

import type { Entities_Player_WDYKTemplateQuestions_WDYKQuestion } from '@suntex.fim/api-client'

declare const MathJax:
  | { typesetPromise: (elements?: unknown[]) => Promise<void> }
  | undefined

interface WDYKQuestionPreviewProps {
  question: Pick<
    Entities_Player_WDYKTemplateQuestions_WDYKQuestion,
    'question' | 'image' | 'choices'
  >
}

function hasVisibleContent(html: string) {
  const { content } = Object.assign(document.createElement('template'), {
    innerHTML: html
  })
  return (
    !!content.textContent?.trim() ||
    !!content.querySelector('img[src], svg, math')
  )
}

export default function WDYKQuestionPreview({
  question
}: WDYKQuestionPreviewProps) {
  const ref = useRef<HTMLDivElement>(null)
  const clean = DOMPurify.sanitize(question.question)
  const choices = question.choices
    .map((choice) => DOMPurify.sanitize(choice))
    .filter(hasVisibleContent)

  useEffect(() => {
    const node = ref.current
    if (node && typeof MathJax !== 'undefined') {
      Promise.resolve()
        .then(() => MathJax.typesetPromise([node]))
        .catch(() => undefined)
    }
  }, [clean, question.choices])

  return (
    <div ref={ref}>
      {parse(clean)}
      {question.image && (
        <img
          src={fimCdnAnimateUrl(`/images/know-and-show/${question.image}`)}
          alt="Question illustration"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      )}
      {choices.length > 0 && (
        <ol style={{ overflowWrap: 'anywhere' }}>
          {choices.map((choice, index) => (
            <li key={index}>{parse(choice)}</li>
          ))}
        </ol>
      )}
    </div>
  )
}
